import {
  REVIEW_KIND_LABELS,
  REVIEW_QUEUE_KINDS,
  type ReviewQueueItem,
  type ReviewQueueKind,
  type ReviewQueueState,
} from '@fruuit/shared'
import {
  Badge,
  Button,
  Callout,
  type Column,
  DataTable,
  EmptyState,
  PageHeader,
  Select,
  StatusBadge,
  Tabs,
  UiLink,
} from '@fruuit/ui'
import { ShieldCheck, UserRoundX } from 'lucide-react'
import { useState } from 'react'
import { errorMessage } from '../../api/client'
import { useClients, useRecordList } from '../../features/records/api'
import { formatDate } from '../../features/records/format'
import { useReviewQueue } from '../../features/review/api'
import { ReviewDrawer, recordHref } from '../../features/review/ReviewDrawer'
import { WorkflowStrip } from '../../features/review/WorkflowStrip'
import { useTitle } from '../../lib/useTitle'

const PAGE_SIZE = 25

const STATE_OPTIONS: { value: ReviewQueueState; label: string }[] = [
  { value: 'in_review', label: 'In review' },
  { value: 'approved', label: 'Approved, awaiting publication' },
  { value: 'pending_release', label: 'Unreleased changes' },
  { value: 'draft', label: 'My drafts' },
  { value: 'returned', label: 'Returned to me' },
]

function stateBadge(item: ReviewQueueItem) {
  if (item.state === 'pending_release') {
    return <StatusBadge status="not released" label="Unreleased changes" />
  }
  return <StatusBadge status={item.state} />
}

/** S14 Review & release (R-07): queue, filters, detail drawer and review actions. */
export function ReviewPage() {
  useTitle('Review & release')
  const [kind, setKind] = useState<ReviewQueueKind | 'all'>('all')
  const [state, setState] = useState<ReviewQueueState | ''>('')
  const [client, setClient] = useState('')
  const [engagement, setEngagement] = useState('')
  const [cursors, setCursors] = useState<(string | undefined)[]>([undefined])
  const [selected, setSelected] = useState<ReviewQueueItem | null>(null)
  const cursor = cursors[cursors.length - 1]
  const clients = useClients()
  const engagements = useRecordList('engagements', { limit: 100, sort: 'title', dir: 'asc' })

  const queue = useReviewQueue({
    limit: PAGE_SIZE,
    ...(cursor ? { cursor } : {}),
    ...(kind !== 'all' ? { kind } : {}),
    ...(state ? { state } : {}),
    ...(client ? { client_org_id: client } : {}),
    ...(engagement ? { engagement_id: engagement } : {}),
  })
  const items = queue.data?.items ?? []
  const counts = queue.data?.counts ?? {}
  const total = Object.values(counts).reduce((sum, value) => sum + (value ?? 0), 0)

  function resetPaging() {
    setCursors([undefined])
  }

  const columns: Column<ReviewQueueItem>[] = [
    {
      id: 'record',
      header: 'Record',
      cell: (item) => (
        <div className="min-w-0">
          <p className="text-xs font-semibold tracking-[0.06em] text-sage uppercase">
            {item.label}
          </p>
          <p className="font-semibold text-ink">{item.title}</p>
          <p className="text-xs text-sage">
            {[
              item.issuer_name !== item.title && item.issuer_name,
              item.engagement_title !== item.title && item.engagement_title,
            ]
              .filter(Boolean)
              .join(' · ') || '—'}
          </p>
        </div>
      ),
    },
    {
      id: 'state',
      header: 'State',
      cell: (item) => (
        <div className="flex flex-col items-start gap-1.5">
          {stateBadge(item)}
          {item.caller_is_contributor && ['in_review', 'approved'].includes(item.state) ? (
            <Badge tone="watch" icon={<UserRoundX />}>
              You contributed
            </Badge>
          ) : null}
        </div>
      ),
    },
    {
      id: 'client',
      header: 'Client',
      cell: (item) => (
        <span className="text-sm text-ink-soft">{item.client_org_name ?? 'Shared'}</span>
      ),
    },
    {
      id: 'author',
      header: 'Submitted',
      cell: (item) => (
        <div className="text-sm">
          <p className="text-ink">{item.submitted_by?.name ?? item.contributors[0]?.name ?? '—'}</p>
          <p className="text-xs text-sage">
            {item.submitted_at
              ? formatDate(item.submitted_at)
              : `Updated ${formatDate(item.updated_at)}`}
          </p>
        </div>
      ),
    },
    {
      id: 'open',
      header: <span className="sr-only">Open</span>,
      align: 'right',
      cell: (item) => {
        if (item.kind === 'minutes') {
          const href = recordHref(item)
          return href ? (
            <UiLink
              href={href}
              className="text-sm font-semibold whitespace-nowrap text-teal hover:underline"
            >
              Review on meeting page
            </UiLink>
          ) : null
        }
        return (
          <Button size="sm" variant="secondary" onClick={() => setSelected(item)}>
            Read full record
          </Button>
        )
      },
    },
  ]

  const filtered = Boolean(state || client || engagement)

  const body = (
    <>
      <div className="mb-4 grid gap-3 sm:grid-cols-3">
        <Select
          label="State"
          value={state}
          placeholder="Awaiting review or release"
          options={STATE_OPTIONS}
          onChange={(event) => {
            setState(event.target.value as ReviewQueueState | '')
            resetPaging()
          }}
        />
        <Select
          label="Client"
          value={client}
          placeholder="All clients"
          options={[
            { value: 'shared', label: 'Shared with all subscribers' },
            ...(clients.data?.items ?? []).map((org) => ({ value: org.id, label: org.name })),
          ]}
          onChange={(event) => {
            setClient(event.target.value)
            resetPaging()
          }}
        />
        <Select
          label="Engagement"
          value={engagement}
          placeholder="All engagements"
          options={(engagements.data?.items ?? []).map((row) => ({
            value: row.id,
            label: row.title,
          }))}
          onChange={(event) => {
            setEngagement(event.target.value)
            resetPaging()
          }}
        />
      </div>
      {queue.isError ? (
        <Callout tone="danger" role="alert" className="mb-4">
          {errorMessage(queue.error)}
        </Callout>
      ) : null}
      <DataTable
        caption="Records awaiting review or release"
        columns={columns}
        rows={items}
        getRowId={(item) => `${item.kind}:${item.id}`}
        loading={queue.isLoading}
        onRowClick={(item) => {
          if (item.kind !== 'minutes') setSelected(item)
        }}
        empty={
          <EmptyState
            variant="plain"
            icon={<ShieldCheck />}
            title={filtered ? 'No records match these filters' : 'No records awaiting review'}
            description={
              filtered
                ? 'Change or clear the filters to see more of the queue.'
                : 'Published records are available in the research and company views.'
            }
          />
        }
        pagination={{
          mode: 'server',
          hasPrevious: cursors.length > 1,
          hasNext: Boolean(queue.data?.next_cursor),
          onPrevious: () => setCursors((current) => current.slice(0, -1)),
          onNext: () => {
            const next = queue.data?.next_cursor
            if (next) setCursors((current) => [...current, next])
          },
          summary: `Page ${cursors.length}`,
        }}
      />
    </>
  )

  const tabItems = [
    { id: 'all', label: 'All', count: total, content: body },
    ...REVIEW_QUEUE_KINDS.map((value) => ({
      id: value,
      label: REVIEW_KIND_LABELS[value].plural,
      count: counts[value] ?? 0,
      content: body,
    })),
  ]

  return (
    <>
      <PageHeader
        eyebrow="Quality & publication"
        title="A second pair of eyes."
        description="The author submits. A different reviewer approves. Client visibility starts only after explicit publication."
      />
      <WorkflowStrip className="mb-6" />
      <Tabs
        label="Record kinds"
        value={kind}
        onValueChange={(next) => {
          setKind(next as ReviewQueueKind | 'all')
          resetPaging()
        }}
        items={tabItems}
      />
      <Callout className="mt-6">
        Anyone who edited a draft is recorded as a contributor and cannot approve, publish, return
        or retract it. Approval and publication are recorded separately, with reasons.
      </Callout>
      <ReviewDrawer item={selected} onClose={() => setSelected(null)} />
    </>
  )
}
